import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { authApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, CheckCircle, XCircle, Loader, ShieldCheck } from 'lucide-react';

const REQUIREMENTS = [
  { label: 'At least 10 characters',          test: p => p.length >= 10 },
  { label: 'One uppercase letter (A–Z)',       test: p => /[A-Z]/.test(p) },
  { label: 'One lowercase letter (a–z)',       test: p => /[a-z]/.test(p) },
  { label: 'One number (0–9)',                 test: p => /[0-9]/.test(p) },
  { label: 'One special character (!@#$…)',    test: p => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p) },
];

export default function SetPassword() {
  const [params]            = useSearchParams();
  const navigate            = useNavigate();
  const { setUser } = useAuth();
  const token               = params.get('token');
  const mode                = params.get('mode') || 'invite'; // 'invite' or 'reset'

  const [tokenInfo, setTokenInfo]   = useState(null);   // { email, full_name }
  const [tokenError, setTokenError] = useState(null);
  const [validating, setValidating] = useState(true);

  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [showPw, setShowPw]         = useState(false);
  const [showConf, setShowConf]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [done, setDone]             = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenError('No token provided. Please use the link from your email.');
      setValidating(false);
      return;
    }
    authApi.validateInviteToken(token)
      .then(({ data }) => { setTokenInfo(data); setValidating(false); })
      .catch(err => {
        setTokenError(err.response?.data?.detail || 'This link is invalid or has expired.');
        setValidating(false);
      });
  }, [token]);

  const allMet    = REQUIREMENTS.every(r => r.test(password));
  const matches   = password && confirm && password === confirm;
  const canSubmit = allMet && matches && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await authApi.setPassword(token, password, confirm);
      // Store token and update auth context so ProtectedRoute sees the user immediately
      localStorage.setItem('token', data.access_token);
      setUser(data.user);
      setDone(true);
      // Small delay so the success message is visible, then navigate
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (validating) return (
    <div className="login-screen">
      <div style={{ textAlign: 'center', color: 'var(--text-dim)' }}>
        <Loader size={28} className="spin" style={{ margin: '0 auto 12px' }} />
        <p>Validating your link…</p>
      </div>
    </div>
  );

  // ── Invalid token ──────────────────────────────────────────────────────────
  if (tokenError) return (
    <div className="login-screen">
      <div className="login-card" style={{ maxWidth: 440, textAlign: 'center' }}>
        <XCircle size={40} style={{ color: 'var(--red)', margin: '0 auto 16px' }} />
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
          Link Expired or Invalid
        </h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
          {tokenError}
        </p>
        <Link to="/forgot-password"
          style={{ display: 'inline-block', padding: '10px 24px', background: 'var(--blue)',
                   color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
          Request a new link
        </Link>
        <div style={{ marginTop: 16 }}>
          <Link to="/login" style={{ fontSize: 13, color: 'var(--text-dim)' }}>← Back to login</Link>
        </div>
      </div>
    </div>
  );

  // ── Success ────────────────────────────────────────────────────────────────
  if (done) return (
    <div className="login-screen">
      <div className="login-card" style={{ maxWidth: 440, textAlign: 'center' }}>
        <CheckCircle size={44} style={{ color: 'var(--green)', margin: '0 auto 16px' }} />
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
          Password set!
        </h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 14 }}>
          Signing you in…
        </p>
      </div>
    </div>
  );

  // ── Set password form ──────────────────────────────────────────────────────
  const isInvite = mode === 'invite';

  return (
    <div className="login-screen">
      <div className="login-card" style={{ maxWidth: 460 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 4px', color: 'var(--text)' }}>
            AP21<span style={{ color: 'var(--blue)' }}>→</span>EZY
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: 0 }}>Sync Platform</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <ShieldCheck size={18} style={{ color: 'var(--blue)' }} />
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            {isInvite ? 'Set your password' : 'Reset your password'}
          </h2>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 24, lineHeight: 1.5 }}>
          {isInvite
            ? `Welcome${tokenInfo?.full_name ? `, ${tokenInfo.full_name.split(' ')[0]}` : ''}! Set a password for `
            : 'Set a new password for '}
          <strong style={{ color: 'var(--text)' }}>{tokenInfo?.email}</strong>
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Password field */}
          <div>
            <label className="input-label">New password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="input"
                placeholder="Enter your new password"
                autoComplete="new-password"
                style={{ paddingRight: 44 }}
              />
              <button type="button" onClick={() => setShowPw(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                         background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0 }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Confirm field */}
          <div>
            <label className="input-label">Confirm password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConf ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="input"
                placeholder="Re-enter your new password"
                autoComplete="new-password"
                style={{ paddingRight: 44 }}
              />
              <button type="button" onClick={() => setShowConf(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                         background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0 }}>
                {showConf ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirm && !matches && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--red)' }}>
                Passwords do not match
              </p>
            )}
          </div>

          {/* Strength checklist */}
          {password && (
            <div style={{ background: 'var(--navy)', borderRadius: 8,
                          border: '1px solid var(--navy-border)', padding: '12px 16px' }}>
              <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 600,
                           color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Password requirements
              </p>
              {REQUIREMENTS.map(req => {
                const met = req.test(password);
                return (
                  <div key={req.label} style={{ display: 'flex', alignItems: 'center', gap: 8,
                                                 marginBottom: 6, fontSize: 13 }}>
                    {met
                      ? <CheckCircle size={13} style={{ color: 'var(--green)', flexShrink: 0 }} />
                      : <XCircle    size={13} style={{ color: 'var(--red)',   flexShrink: 0 }} />}
                    <span style={{ color: met ? 'var(--text-mid)' : 'var(--text-dim)' }}>
                      {req.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderRadius: 8,
                          background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)',
                          fontSize: 13, color: 'var(--red)' }}>
              <XCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {error}
            </div>
          )}

          <button type="submit" disabled={!canSubmit} className="btn btn-primary"
            style={{ marginTop: 4, opacity: canSubmit ? 1 : 0.5 }}>
            {submitting
              ? <><Loader size={15} className="spin" /> Setting password…</>
              : isInvite ? 'Set Password & Sign In' : 'Reset Password & Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/login" style={{ fontSize: 13, color: 'var(--text-dim)' }}>
            ← Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
